import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { IconTrash, IconPlus } from '../../components/Icons';

/**
 * Installation pricing calculator + quote builder (Super-Admin only).
 *
 * Model (the owner's): round-trip travel × number of days + work hours
 * (gates × time-per-gate) + the parts each gate needs.
 *   gatesPerDay = floor((workday − roundTripTravel) / timePerGate)
 *   days        = ceil(totalGates / gatesPerDay)        ← a far site that
 *                 needs a 2nd trip carries a 2nd travel charge.
 * Pick a gate by model; the model auto-pulls its components (Shelly +
 * the matching card/transformer) from the catalog. Costs stay internal;
 * the quote shows prices only. Travel km/time are entered by hand for
 * now (Google Maps auto-fill is a later step).
 */

const ILS = (n) => `₪${(Math.round((n || 0) * 100) / 100).toLocaleString('he-IL')}`;
const pct = (n) => `${Math.round(n || 0)}%`;
let _seq = 1;
const nid = () => _seq++;

function PricingCalculator() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');

  const [settings, setSettings] = useState(null);
  const [parts, setParts] = useState([]);
  const [trip, setTrip] = useState({ address: '', km: 0, driveH: 1, driveM: 0, techs: 1 });
  const [customer, setCustomer] = useState({ name: '', contact: '', phone: '' });
  const [gateLines, setGateLines] = useState([]);
  const [extraParts, setExtraParts] = useState([]);
  const [laborActions, setLaborActions] = useState([]);
  const [showManage, setShowManage] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [distLoading, setDistLoading] = useState(false);
  const [distErr, setDistErr] = useState('');
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [quoteNo, setQuoteNo] = useState('');
  const [currentQuoteId, setCurrentQuoteId] = useState(null);

  const loadQuotes = useCallback(() => {
    axios.get('/api/admin/pricing/quotes').then(res => setSavedQuotes(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    axios.get('/api/admin/pricing/config')
      .then(res => {
        setConfig(res.data); setSettings(res.data.settings); setParts(res.data.parts);
        setGateLines(prev => prev.length ? prev : [{ id: nid(), modelKey: res.data.gate_models[0].key, qty: 1 }]);
        loadQuotes();
      })
      .catch(err => {
        const s = err.response?.status;
        setError(s === 403 ? 'הכלי זמין למנהל ראשי בלבד.' : `טעינה נכשלה (${s || 'ללא תגובה'}).`);
      });
  }, [loadQuotes]);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setSaveMsg(m); setTimeout(() => setSaveMsg(''), 2200); };

  const partsByKey = useMemo(() => {
    const m = {}; parts.forEach(p => { if (p.key) m[p.key] = p; }); return m;
  }, [parts]);
  const modelByKey = useMemo(() => {
    const m = {}; (config?.gate_models || []).forEach(g => { m[g.key] = g; }); return m;
  }, [config]);

  // --- the installation pricing model -------------------------------------
  const calc = useMemo(() => {
    if (!settings || !config) return null;
    const toILS = (amt, cur) => cur === 'EUR' ? (amt || 0) * (settings.eur_to_ils || 0) : (amt || 0);
    const crew2 = trip.techs === 2;
    const travelRate = crew2 ? settings.travel_hour_rate_2 : settings.travel_hour_rate_1;
    const workRate = crew2 ? settings.work_hour_rate_2 : settings.work_hour_rate_1;

    // per gate-model line: cost + price of its components × qty
    const gateRows = gateLines.map(l => {
      const m = modelByKey[l.modelKey];
      const q = Number(l.qty) || 0;
      let unitCost = 0, unitPrice = 0; const names = [];
      if (m) m.components.forEach(ck => {
        const p = partsByKey[ck];
        if (p) { unitCost += toILS(p.cost_amount, p.currency); unitPrice += p.customer_price || 0; names.push(p.name); }
      });
      const lineCost = unitCost * q, linePrice = unitPrice * q;
      return { id: l.id, modelKey: l.modelKey, name: m ? m.name : '', comp: names.join(' + '),
        qty: q, unitCost, unitPrice, lineCost, linePrice,
        margin: linePrice > 0 ? ((linePrice - lineCost) / linePrice) * 100 : null };
    });
    const extraRows = extraParts.map(e => {
      const qty = Number(e.qty) || 0, price = Number(e.price) || 0, cost = toILS(Number(e.cost) || 0, e.currency);
      return { id: e.id, name: e.name, qty, mins: Number(e.mins) || 0, unitCost: cost, unitPrice: price,
        lineCost: cost * qty, linePrice: price * qty,
        margin: price > 0 ? ((price - cost) / price) * 100 : null };
    });
    const totalGates = gateRows.reduce((s, r) => s + r.qty, 0);
    let partsPrice = 0, partsCost = 0;
    [...gateRows, ...extraRows].forEach(r => { partsPrice += r.linePrice; partsCost += r.lineCost; });

    // --- labour: travel + itemised work lines -------------------------------
    const crew = crew2 ? 2 : 1;
    const driveOneWay = (Number(trip.driveH) || 0) + (Number(trip.driveM) || 0) / 60;
    const roundTripTravelH = driveOneWay * (settings.travel_buffer || 1) * 2;
    const km = Number(trip.km) || 0;
    const travelPerTrip = km * 2 * (settings.price_per_km || 0) + roundTripTravelH * (travelRate || 0);
    const techCost = settings.tech_hour_cost || 0;

    // One work line per gate model in the quote (auto) + manual actions.
    const workLines = [
      ...gateRows.filter(r => r.qty > 0).map(r => ({
        id: 'g_' + r.id, name: r.name, minutes: settings.minutes_per_gate || 0,
        qty: r.qty, auto: true,
      })),
      ...extraRows.filter(r => r.qty > 0 && r.mins > 0).map(r => ({
        id: 'e_' + r.id, name: `התקנה — ${r.name}`, minutes: r.mins, qty: r.qty, auto: true,
      })),
      ...laborActions.map(a => ({
        id: a.id, name: a.name, minutes: Number(a.minutes) || 0, qty: Number(a.qty) || 0, auto: false,
      })),
    ].map(l => {
      const hours = l.qty * (l.minutes || 0) / 60;
      return { ...l, hours, cost: hours * crew * techCost, price: hours * (workRate || 0) };
    });
    const workHours = workLines.reduce((s, l) => s + l.hours, 0);
    const workCostOurs = workLines.reduce((s, l) => s + l.cost, 0);
    const workPriceCust = workLines.reduce((s, l) => s + l.price, 0);

    // Visits: how many round trips the work needs given the net day.
    const availPerDay = Math.max((settings.workday_hours || 0) - roundTripTravelH, 0);
    const feasible = availPerDay > 0 || workHours === 0;
    const days = workHours > 0 ? (availPerDay > 0 ? Math.ceil(workHours / availPerDay) : 1) : 0;

    const travelCostOurs = (roundTripTravelH * crew * techCost + km * 2 * (settings.fuel_cost_per_km || 0)) * days;
    const travelPriceCust = travelPerTrip * days;

    const laborCostOurs = workCostOurs + travelCostOurs;
    // No round-to-10: real figures only, to the whole shekel.
    const laborTotal = Math.round(workPriceCust + travelPriceCust);
    const laborMargin = laborTotal > 0 ? ((laborTotal - laborCostOurs) / laborTotal) * 100 : null;

    // Internal truth: parts + travel/work.
    const subtotal = laborTotal + partsPrice;

    // Customer view (B2B): fold travel+work into the per-item prices — no
    // separate labour line that invites haggling. Spread the labour across
    // all item quantities; round each unit to the whole shekel only (real,
    // non-round numbers — never to 10).
    const totalQty = [...gateRows, ...extraRows].reduce((s, r) => s + (r.qty > 0 ? r.qty : 0), 0);
    const laborPerItem = totalQty > 0 ? laborTotal / totalQty : 0;
    const embed = (r) => {
      const custUnit = Math.round(r.unitPrice + laborPerItem);
      const custLine = custUnit * r.qty;
      return { ...r, custUnit, custLine, custMargin: custLine > 0 ? ((custLine - r.lineCost) / custLine) * 100 : null };
    };
    const gateRowsE = gateRows.map(embed);
    const extraRowsE = extraRows.map(embed);
    const customerLines = [...gateRowsE, ...extraRowsE].filter(r => r.qty > 0)
      .map(r => ({ name: r.name, qty: r.qty, unit: r.custUnit, total: r.custLine }));
    const customerTotal = customerLines.reduce((s, r) => s + r.total, 0);

    const vat = customerTotal * ((settings.vat_percent || 0) / 100);
    const partsMargin = partsPrice > 0 ? ((partsPrice - partsCost) / partsPrice) * 100 : null;
    const custMargin = customerTotal > 0 ? ((customerTotal - partsCost) / customerTotal) * 100 : null;
    return {
      crew, totalGates, km, travelPerTrip, days, roundTripTravelH, feasible,
      workLines, workHours, workCostOurs, workPriceCust,
      travelCostOurs, travelPriceCust, laborCostOurs, laborTotal, laborMargin,
      gateRows: gateRowsE, extraRows: extraRowsE,
      partsPrice, partsCost, subtotal, customerLines, customerTotal, laborPerItem, vat,
      total: customerTotal, partsMargin, custMargin,
    };
  }, [settings, config, trip, gateLines, extraParts, laborActions, partsByKey, modelByKey]);

  if (error) return <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>{error}</div>;
  if (!config || !settings || !calc) return <div className="loading">טוען...</div>;

  const t = config.thresholds;
  const marginColor = (m) => m == null ? '#9ca3af' : m >= t.green ? '#16a34a' : m >= t.yellow ? '#d97706' : '#dc2626';
  const setS = (patch) => setSettings({ ...settings, ...patch });

  const saveSettings = () => axios.put('/api/admin/pricing/settings', settings)
    .then(res => { setSettings(res.data.settings); flash('התעריפים נשמרו'); }).catch(() => flash('שמירה נכשלה'));
  const setPartField = (id, patch) => setParts(parts.map(p => p.id === id ? { ...p, ...patch } : p));
  const savePart = (id) => { const p = parts.find(x => x.id === id); if (!p) return;
    axios.put(`/api/admin/pricing/items/${id}`, p).then(res => setPartField(id, res.data)).catch(err => flash(err.response?.data?.error || 'שמירה נכשלה')); };
  const addPart = () => axios.post('/api/admin/pricing/items', { name: 'פריט חדש', cost_amount: 0, currency: 'ILS' })
    .then(res => { setParts([...parts, res.data]); flash('פריט נוסף'); }).catch(() => flash('הוספה נכשלה'));
  const deletePart = (id) => axios.delete(`/api/admin/pricing/items/${id}`)
    .then(() => setParts(parts.filter(p => p.id !== id))).catch(err => flash(err.response?.data?.error || 'מחיקה נכשלה'));

  const addGateLine = () => setGateLines([...gateLines, { id: nid(), modelKey: config.gate_models[0].key, qty: 1 }]);
  const updGateLine = (id, patch) => setGateLines(gateLines.map(l => l.id === id ? { ...l, ...patch } : l));
  const rmGateLine = (id) => setGateLines(gateLines.filter(l => l.id !== id));
  const addExtra = (partId) => { const p = parts.find(x => String(x.id) === String(partId)); if (!p) return;
    setExtraParts([...extraParts, { id: nid(), name: p.name, qty: 1, price: p.customer_price || 0, cost: p.cost_amount, currency: p.currency, mins: p.install_minutes || 0 }]); };
  const updExtra = (id, patch) => setExtraParts(extraParts.map(l => l.id === id ? { ...l, ...patch } : l));
  const rmExtra = (id) => setExtraParts(extraParts.filter(l => l.id !== id));
  const addAction = () => setLaborActions([...laborActions, { id: nid(), name: 'פעולה חדשה', minutes: 30, qty: 1 }]);
  const updAction = (id, patch) => setLaborActions(laborActions.map(a => a.id === id ? { ...a, ...patch } : a));
  const rmAction = (id) => setLaborActions(laborActions.filter(a => a.id !== id));

  const genQuoteNo = () => {
    const d = new Date();
    return `Q-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 900) + 100)}`;
  };
  const openPreview = () => { if (!quoteNo) setQuoteNo(genQuoteNo()); setShowQuote(true); };
  const newQuote = () => {
    setCurrentQuoteId(null); setQuoteNo('');
    setTrip({ address: '', km: 0, driveH: 1, driveM: 0, techs: 1 });
    setCustomer({ name: '', contact: '', phone: '' });
    setGateLines([{ id: nid(), modelKey: config.gate_models[0].key, qty: 1 }]);
    setExtraParts([]); setLaborActions([]); flash('הצעה חדשה');
  };
  const saveQuote = () => {
    const no = quoteNo || genQuoteNo();
    if (!quoteNo) setQuoteNo(no);
    const body = { quote_no: no, customer_name: customer.name, total: calc.total,
      payload: { trip, customer, gateLines, extraParts, laborActions } };
    const req = currentQuoteId
      ? axios.put(`/api/admin/pricing/quotes/${currentQuoteId}`, body)
      : axios.post('/api/admin/pricing/quotes', body);
    req.then(res => { if (!currentQuoteId) setCurrentQuoteId(res.data.id); flash(currentQuoteId ? 'ההצעה עודכנה' : 'ההצעה נשמרה'); loadQuotes(); })
      .catch(() => flash('שמירה נכשלה'));
  };
  const openQuote = (id) => {
    axios.get(`/api/admin/pricing/quotes/${id}`).then(res => {
      const p = res.data.payload || {};
      if (p.trip) setTrip(p.trip);
      if (p.customer) setCustomer(p.customer);
      setGateLines((p.gateLines || []).map(l => ({ ...l, id: nid() })));
      setExtraParts((p.extraParts || []).map(l => ({ ...l, id: nid() })));
      setLaborActions((p.laborActions || []).map(a => ({ ...a, id: nid() })));
      setQuoteNo(res.data.quote_no || '');
      setCurrentQuoteId(id);
      flash('ההצעה נטענה'); window.scrollTo({ top: 0, behavior: 'smooth' });
    }).catch(() => flash('טעינה נכשלה'));
  };
  const deleteQuote = (id) => {
    if (!window.confirm('למחוק את ההצעה? לא ניתן לשחזר.')) return;
    axios.delete(`/api/admin/pricing/quotes/${id}`)
      .then(() => { if (currentQuoteId === id) { setCurrentQuoteId(null); setQuoteNo(''); } loadQuotes(); })
      .catch(() => flash('מחיקה נכשלה'));
  };

  const lookupDistance = () => {
    if (!trip.address.trim()) { setDistErr('הזן כתובת קודם.'); return; }
    setDistErr(''); setDistLoading(true);
    axios.get('/api/admin/pricing/distance', { params: { address: trip.address } })
      .then(res => {
        const h = res.data.hours || 0;
        setTrip(t => ({ ...t, km: res.data.km, driveH: Math.floor(h), driveM: Math.round((h - Math.floor(h)) * 60) }));
      })
      .catch(err => setDistErr(err.response?.data?.error || 'החישוב נכשל'))
      .finally(() => setDistLoading(false));
  };

  return (
    <>
      <style>{`@media print {
        @page { margin: 1.4cm; }
        /* Hide ALL app chrome + the calculator screen + action buttons. */
        .sidebar, .header, .pricing-main, .no-print { display: none !important; }
        /* Drop the quote out of the floating modal into normal flow so it
           paginates cleanly (no repeated fixed overlay, no clipping). */
        .app-layout, .main-content, .page-content { display: block !important; overflow: visible !important; }
        .modal-overlay { position: static !important; display: block !important;
          background: none !important; padding: 0 !important; overflow: visible !important; }
        .modal-overlay > div { width: 100% !important; max-width: 100% !important; }
        .quote-print { box-shadow: none !important; border: 0 !important;
          border-radius: 0 !important; padding: 0 !important; }
        .quote-section { page-break-inside: avoid; }
      }`}</style>

      <div className="pricing-main">
      <div className="breadcrumb"><span>תמחור</span></div>
      <div className="section-header">
        <h3>מחשבון תמחור התקנה</h3>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {saveMsg && <span style={{ color: '#16a34a', fontSize: '0.82rem' }}>{saveMsg}</span>}
          {currentQuoteId && <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>עריכת {quoteNo}</span>}
          {currentQuoteId && <button className="btn btn-secondary btn-sm" onClick={newQuote}>הצעה חדשה</button>}
          <button className="btn btn-secondary btn-sm" onClick={saveQuote}>{currentQuoteId ? 'עדכן הצעה' : 'שמור הצעה'}</button>
          <button className="btn btn-primary btn-sm" onClick={openPreview}>הפק הצעת מחיר (PDF)</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* gates */}
          <div className="info-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>שערים להתקנה</h3>
              <button className="btn btn-secondary btn-sm" onClick={addGateLine}><IconPlus size={14} /> הוסף דגם</button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 4 }}>בחר דגם וכמות — הרכיבים נגזרים אוטומטית. "מחיר ללקוח" כולל את הנסיעה והעבודה מגולמות בפריט (לכל הכמות).</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}>
                  <th style={th}>דגם השער</th><th style={thC}>כמות</th><th style={thC}>עלות</th><th style={thC}>מחיר ללקוח</th><th style={thC}>רווח</th><th style={thC}></th>
                </tr></thead>
                <tbody>
                  {calc.gateRows.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid #f0f0f3' }}>
                      <td style={td}>
                        <select value={r.modelKey} onChange={e => updGateLine(r.id, { modelKey: e.target.value })}
                          style={{ width: '100%', padding: '0.35rem', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit' }}>
                          {config.gate_models.map(g => <option key={g.key} value={g.key}>{g.name}</option>)}
                        </select>
                        <div style={{ color: '#9ca3af', fontSize: '0.72rem', marginTop: 2 }}>{r.comp}</div>
                      </td>
                      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 60 }} value={gateLines.find(l => l.id === r.id)?.qty ?? 0} onChange={e => updGateLine(r.id, { qty: e.target.value })} /></td>
                      <td style={{ ...tdC, color: '#6b7280' }}>{ILS(r.lineCost)}</td>
                      <td style={{ ...tdC, fontWeight: 600 }}>{ILS(r.custLine)}</td>
                      <td style={tdC}>{r.custMargin == null ? '—' : <span style={{ color: marginColor(r.custMargin), fontWeight: 700 }}>{pct(r.custMargin)}</span>}</td>
                      <td style={tdC}><button onClick={() => rmGateLine(r.id)} title="הסר" style={ibtn}><IconTrash size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
                    <td style={td}>סה"כ</td>
                    <td style={tdC}>{calc.totalGates}</td>
                    <td style={{ ...tdC, color: '#6b7280' }}>{ILS(calc.partsCost)}</td>
                    <td style={tdC}>{ILS(calc.customerTotal)}</td>
                    <td style={tdC}>{calc.custMargin == null ? '—' : <span style={{ color: marginColor(calc.custMargin) }}>{pct(calc.custMargin)}</span>}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* trip */}
          <div className="info-card">
            <h3 style={{ marginTop: 0 }}>נסיעה וצוות</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Field label="כתובת הקריאה" grow>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={txt} value={trip.address} onChange={e => setTrip({ ...trip, address: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter' && config.maps_enabled) lookupDistance(); }} placeholder="עיר / כתובת" />
                  {config.maps_enabled && (
                    <button className="btn btn-secondary btn-sm" onClick={lookupDistance} disabled={distLoading} style={{ whiteSpace: 'nowrap' }}>
                      {distLoading ? 'מחשב…' : '📍 חשב'}
                    </button>
                  )}
                </div>
              </Field>
              <Field label="מרחק (ק״מ, חד-כיווני)"><input type="number" min="0" style={num} value={trip.km} onChange={e => setTrip({ ...trip, km: e.target.value })} /></Field>
              <Field label="זמן נסיעה (חד-כיווני)">
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="number" min="0" style={{ ...num, width: 54 }} value={trip.driveH} onChange={e => setTrip({ ...trip, driveH: e.target.value })} /><span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>ש'</span>
                  <input type="number" min="0" max="59" style={{ ...num, width: 54 }} value={trip.driveM} onChange={e => setTrip({ ...trip, driveM: e.target.value })} /><span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק'</span>
                </div>
              </Field>
              <Field label="מס׳ טכנאים">
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2].map(n => <button key={n} onClick={() => setTrip({ ...trip, techs: n })} className={trip.techs === n ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} style={{ minWidth: 46 }}>{n}</button>)}
                </div>
              </Field>
            </div>
            <div style={{ fontSize: '0.74rem', color: '#9ca3af', marginTop: 6 }}>
              {config.maps_enabled
                ? `לחץ "חשב" למילוי ק"מ וזמן נסיעה אוטומטית ממוצא ${settings.origin_address} (ניתן לערוך ידנית).`
                : `הזן ק"מ וזמן ידנית (מ-Waze / Google Maps), ממוצא ${settings.origin_address}.`}
            </div>
            {distErr && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: 4 }}>{distErr}</div>}

            {/* itemised labour calculator */}
            <div style={{ marginTop: '1rem', paddingTop: '0.85rem', borderTop: '1px solid #eef0f3' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem' }}>חישוב עבודה</strong>
                <button className="btn btn-secondary btn-sm" onClick={addAction}><IconPlus size={14} /> הוסף פעולה</button>
              </div>
              <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}>
                    <th style={th}>פעולה</th><th style={thC}>דק' ליח'</th><th style={thC}>כמות</th><th style={thC}>עלות לנו</th><th style={thC}>מחיר ללקוח</th><th style={thC}></th>
                  </tr></thead>
                  <tbody>
                    {/* travel row */}
                    <tr style={{ borderTop: '1px solid #f0f0f3', background: '#f8fafc' }}>
                      <td style={td}>נסיעה הלוך-חזור{calc.days > 1 ? ` × ${calc.days} ביקורים` : ''}</td>
                      <td style={tdC}>—</td><td style={tdC}>—</td>
                      <td style={{ ...tdC, color: '#6b7280' }}>{ILS(calc.travelCostOurs)}</td>
                      <td style={{ ...tdC, fontWeight: 600 }}>{ILS(calc.travelPriceCust)}</td>
                      <td style={tdC}></td>
                    </tr>
                    {calc.workLines.map(l => (
                      <tr key={l.id} style={{ borderTop: '1px solid #f0f0f3' }}>
                        <td style={td}>
                          {l.auto ? <span>{l.name}</span>
                            : <input style={txt} value={l.name} onChange={e => updAction(l.id, { name: e.target.value })} />}
                        </td>
                        <td style={tdC}>
                          {l.auto ? <span style={{ color: '#9ca3af' }}>{l.minutes}</span>
                            : <input type="number" min="0" style={{ ...num, width: 56 }} value={l.minutes} onChange={e => updAction(l.id, { minutes: e.target.value })} />}
                        </td>
                        <td style={tdC}>
                          {l.auto ? <span style={{ color: '#9ca3af' }}>{l.qty}</span>
                            : <input type="number" min="0" style={{ ...num, width: 56 }} value={l.qty} onChange={e => updAction(l.id, { qty: e.target.value })} />}
                        </td>
                        <td style={{ ...tdC, color: '#6b7280' }}>{ILS(l.cost)}</td>
                        <td style={{ ...tdC, fontWeight: 600 }}>{ILS(l.price)}</td>
                        <td style={tdC}>{l.auto ? '' : <button onClick={() => rmAction(l.id)} title="הסר" style={ibtn}><IconTrash size={16} /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
                      <td style={td}>סה"כ עבודה</td>
                      <td style={tdC}></td>
                      <td style={tdC}>{Math.round(calc.workHours * 10) / 10} ש'</td>
                      <td style={{ ...tdC, color: '#6b7280' }}>{ILS(calc.laborCostOurs)}</td>
                      <td style={tdC}>{ILS(calc.laborTotal)}</td>
                      <td style={tdC}>{calc.laborMargin == null ? '' : <span style={{ color: marginColor(calc.laborMargin) }}>{pct(calc.laborMargin)}</span>}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {calc.days > 1 && <div style={{ fontSize: '0.74rem', color: '#d97706', marginTop: 4 }}>העבודה לא נכנסת ביום אחד — צריך {calc.days} ביקורים, והנסיעה נספרת {calc.days} פעמים.</div>}
              {!calc.feasible && <div style={{ fontSize: '0.74rem', color: '#dc2626', marginTop: 4 }}>זמן הנסיעה אוכל את יום העבודה — בדוק את הנתונים.</div>}
            </div>
          </div>

          {/* customer */}
          <div className="info-card">
            <h3 style={{ marginTop: 0 }}>פרטי הלקוח</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Field label="שם הלקוח / חברה" grow><input style={txt} value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value })} placeholder="שם" /></Field>
              <Field label="איש קשר" grow><input style={txt} value={customer.contact} onChange={e => setCustomer({ ...customer, contact: e.target.value })} /></Field>
              <Field label="טלפון" grow><input style={txt} value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} /></Field>
            </div>
          </div>

          {/* extra parts */}
          <div className="info-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>תוספות (אופציונלי)</h3>
              <select defaultValue="" onChange={e => { if (e.target.value) { addExtra(e.target.value); e.target.value = ''; } }}
                style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit', fontSize: '0.82rem', maxWidth: 220 }}>
                <option value="">+ הוסף תוספת מהמאגר</option>
                {parts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {extraParts.length > 0 && (
              <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}><th style={th}>פריט</th><th style={thC}>כמות</th><th style={thC} title="זמן עבודה — נכנס אוטומטית לחישוב העבודה">דק' עבודה</th><th style={thC}>מחיר ליח׳</th><th style={thC}></th></tr></thead>
                  <tbody>
                    {extraParts.map(l => (
                      <tr key={l.id} style={{ borderTop: '1px solid #f0f0f3' }}>
                        <td style={td}><input style={txt} value={l.name} onChange={e => updExtra(l.id, { name: e.target.value })} /></td>
                        <td style={tdC}><input type="number" min="0" style={{ ...num, width: 56 }} value={l.qty} onChange={e => updExtra(l.id, { qty: e.target.value })} /></td>
                        <td style={tdC}><input type="number" min="0" style={{ ...num, width: 56 }} value={l.mins ?? 0} onChange={e => updExtra(l.id, { mins: e.target.value })} /></td>
                        <td style={tdC}><input type="number" min="0" style={num} value={l.price} onChange={e => updExtra(l.id, { price: e.target.value })} /></td>
                        <td style={tdC}><button onClick={() => rmExtra(l.id)} title="הסר" style={ibtn}><IconTrash size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* price card */}
        <div style={{ flex: '0 0 280px', position: 'sticky', top: '1rem' }}>
          <div className="info-card">
            <h3 style={{ marginTop: 0 }}>תמחור</h3>
            <Row label="סה״כ שערים" v={calc.totalGates} />
            <Row label="שעות עבודה" v={`${Math.round(calc.workHours * 10) / 10} ש'`} />
            <Row label="ימי עבודה (ביקורים)" v={calc.days} bold />
            {calc.days > 1 && <div style={{ fontSize: '0.74rem', color: '#d97706' }}>צריך {calc.days} ביקורים — נסיעה נספרת {calc.days} פעמים.</div>}
            {!calc.feasible && <div style={{ fontSize: '0.74rem', color: '#dc2626' }}>זמן הנסיעה אוכל את יום העבודה — בדוק נתונים.</div>}
            <div style={{ borderTop: '1px dashed #e5e7eb', margin: '0.5rem 0' }} />
            <Row label="נסיעה" v={ILS(calc.travelPriceCust)} />
            <Row label="עבודה" v={ILS(calc.workPriceCust)} />
            <Row label="ביקור + עבודה" v={ILS(calc.laborTotal)} bold />
            {calc.partsPrice > 0 && <Row label="חלקים (מחיר)" v={ILS(calc.partsPrice)} />}
            <div style={{ borderTop: '1px solid #eee', margin: '0.5rem 0' }} />
            {/* internal self-check */}
            <Row label="עלות חלקים" v={ILS(calc.partsCost)} />
            {calc.partsMargin != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.18rem 0', fontSize: '0.85rem' }}>
                <span>רווח על חלקים</span>
                <span style={{ color: marginColor(calc.partsMargin), fontWeight: 700 }}>{pct(calc.partsMargin)}</span>
              </div>
            )}
          </div>
          <div className="info-card" style={{ marginTop: '0.75rem', background: '#1B3A5C', color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>סה"כ ללקוח (לא כולל מע"מ)</div>
            <div style={{ fontSize: '2rem', fontWeight: 800 }}>{ILS(calc.total)}</div>
            <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: 4 }}>מע"מ {settings.vat_percent}% (לידיעה): {ILS(calc.vat)}</div>
            <div style={{ fontSize: '0.68rem', opacity: 0.7, marginTop: 6 }}>הנסיעה והעבודה מגולמות במחירי הפריטים בהצעה</div>
          </div>
        </div>
      </div>

      {/* management */}
      <div className="info-card" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>הצעות שמורות</h3>
        {savedQuotes.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>עדיין אין הצעות שמורות. בנה הצעה ולחץ "שמור הצעה" למעלה.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}>
                <th style={th}>מספר</th><th style={th}>לקוח</th><th style={thC}>סה"כ</th><th style={th}>תאריך</th><th style={thC}></th>
              </tr></thead>
              <tbody>
                {savedQuotes.map(q => (
                  <tr key={q.id} style={{ borderTop: '1px solid #f0f0f3' }}>
                    <td style={td}>{q.quote_no || '—'}</td>
                    <td style={td}>{q.customer_name || '—'}</td>
                    <td style={{ ...tdC, fontWeight: 600 }}>{ILS(q.total)}</td>
                    <td style={td}>{q.created_at ? new Date(q.created_at).toLocaleDateString('he-IL') : ''}</td>
                    <td style={tdC}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openQuote(q.id)}>פתח</button>
                        <button onClick={() => deleteQuote(q.id)} title="מחק" style={ibtn}><IconTrash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowManage(!showManage)}>{showManage ? '▲ הסתר ניהול' : '▼ ניהול: תעריפים ומאגר חלקים'}</button>
      </div>
      {showManage && (
        <>
          <div className="info-card" style={{ marginTop: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>תעריפים</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Field label="כתובת מוצא"><input style={{ ...num, width: 120, textAlign: 'right' }} value={settings.origin_address} onChange={e => setS({ origin_address: e.target.value })} /></Field>
              <Field label="₪ לק״מ"><input type="number" style={num} value={settings.price_per_km} onChange={e => setS({ price_per_km: +e.target.value })} /></Field>
              <Field label="דקות לשער"><input type="number" style={num} value={settings.minutes_per_gate} onChange={e => setS({ minutes_per_gate: +e.target.value })} /></Field>
              <Field label="שעות יום עבודה"><input type="number" step="0.5" style={num} value={settings.workday_hours} onChange={e => setS({ workday_hours: +e.target.value })} /></Field>
              <Field label="מקדם זמן נסיעה"><input type="number" step="0.1" style={num} value={settings.travel_buffer} onChange={e => setS({ travel_buffer: +e.target.value })} /></Field>
              <Field label="עלות שעת טכנאי לנו (₪)"><input type="number" style={num} value={settings.tech_hour_cost} onChange={e => setS({ tech_hour_cost: +e.target.value })} /></Field>
              <Field label="עלות דלק לק״מ (₪)"><input type="number" step="0.1" style={num} value={settings.fuel_cost_per_km} onChange={e => setS({ fuel_cost_per_km: +e.target.value })} /></Field>
              <Field label='מע"מ %'><input type="number" style={num} value={settings.vat_percent} onChange={e => setS({ vat_percent: +e.target.value })} /></Field>
              <Field label="יורו→₪"><input type="number" step="0.01" style={num} value={settings.eur_to_ils} onChange={e => setS({ eur_to_ils: +e.target.value })} /></Field>
              <Field label="תוקף הצעה (ימים)"><input type="number" style={num} value={settings.quote_validity_days} onChange={e => setS({ quote_validity_days: +e.target.value })} /></Field>
            </div>
            <table style={{ width: '100%', maxWidth: 520, borderCollapse: 'collapse', fontSize: '0.85rem', marginTop: '1rem' }}>
              <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}><th style={th}>תעריף</th><th style={thC}>טכנאי אחד</th><th style={thC}>שני טכנאים</th></tr></thead>
              <tbody>
                <RateRow label="שעת נסיעה (₪/שעה)" v1={settings.travel_hour_rate_1} v2={settings.travel_hour_rate_2} on1={v => setS({ travel_hour_rate_1: v })} on2={v => setS({ travel_hour_rate_2: v })} />
                <RateRow label="שעת עבודה (₪/שעה)" v1={settings.work_hour_rate_1} v2={settings.work_hour_rate_2} on1={v => setS({ work_hour_rate_1: v })} on2={v => setS({ work_hour_rate_2: v })} />
              </tbody>
            </table>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '0.95rem' }}>סעיפי ההצעה (מופיעים בהצעת המחיר)</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
              <Field label="תנאי תשלום" grow>
                <textarea rows={2} style={{ ...txt, resize: 'vertical' }} value={settings.payment_terms} onChange={e => setS({ payment_terms: e.target.value })} />
              </Field>
              <Field label="זמן אספקה (ימים)">
                <input type="number" style={num} value={settings.lead_time_days} onChange={e => setS({ lead_time_days: +e.target.value })} />
              </Field>
              <Field label="טלפון תמיכה">
                <input style={{ ...num, width: 130, textAlign: 'right' }} value={settings.support_phone} onChange={e => setS({ support_phone: e.target.value })} />
              </Field>
              <Field label="אחריות ושירות" grow>
                <textarea rows={3} style={{ ...txt, resize: 'vertical' }} value={settings.warranty_text} onChange={e => setS({ warranty_text: e.target.value })} />
              </Field>
            </div>

            <button className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }} onClick={saveSettings}>שמור תעריפים</button>
          </div>

          <div className="info-card" style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>מאגר חלקים</h3>
              <button className="btn btn-secondary btn-sm" onClick={addPart}><IconPlus size={14} /> הוסף פריט</button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 4 }}>נשמר אוטומטית ביציאה מהשדה. 🔒 = רכיב שדגם תלוי בו (עדכון בלבד). מלא מחיר ללקוח.</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead><tr style={{ color: '#6b7280', textAlign: 'right' }}><th style={th}>מק״ט</th><th style={th}>שם</th><th style={thC}>עלות</th><th style={thC}>מטבע</th><th style={thC}>מחיר ללקוח</th><th style={thC} title="זמן התקנה — נכנס אוטומטית לחישוב העבודה כשמוסיפים את הפריט">דק' עבודה</th><th style={thC}></th></tr></thead>
                <tbody>
                  {parts.map(p => (
                    <tr key={p.id} style={{ borderTop: '1px solid #f0f0f3' }}>
                      <td style={td}><input style={{ ...txt, width: 80 }} value={p.sku} onChange={e => setPartField(p.id, { sku: e.target.value })} onBlur={() => savePart(p.id)} /></td>
                      <td style={{ ...td, minWidth: 180 }}><input style={txt} value={p.name} onChange={e => setPartField(p.id, { name: e.target.value })} onBlur={() => savePart(p.id)} /></td>
                      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 80 }} value={p.cost_amount} onChange={e => setPartField(p.id, { cost_amount: e.target.value })} onBlur={() => savePart(p.id)} /></td>
                      <td style={tdC}>
                        <select value={p.currency} onChange={e => setPartField(p.id, { currency: e.target.value })} onBlur={() => savePart(p.id)} style={{ padding: '0.3rem', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit' }}>
                          <option value="ILS">₪</option><option value="EUR">€</option>
                        </select>
                      </td>
                      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 90 }} value={p.customer_price ?? ''} placeholder="—" onChange={e => setPartField(p.id, { customer_price: e.target.value })} onBlur={() => savePart(p.id)} /></td>
                      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 64 }} value={p.install_minutes ?? 0} onChange={e => setPartField(p.id, { install_minutes: e.target.value })} onBlur={() => savePart(p.id)} /></td>
                      <td style={tdC}>{p.protected ? <span title="רכיב קבוע" style={{ color: '#cbd5e1' }}>🔒</span> : <button onClick={() => deletePart(p.id)} title="מחק" style={ibtn}><IconTrash size={16} /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </div>

      {showQuote && <QuotePreview config={config} settings={settings} customer={customer} trip={trip} calc={calc} quoteNo={quoteNo} onClose={() => setShowQuote(false)} />}
    </>
  );
}

const th = { padding: '0.4rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap' };
const thC = { ...th, textAlign: 'center' };
const td = { padding: '0.4rem 0.5rem', verticalAlign: 'middle' };
const tdC = { ...td, textAlign: 'center' };
const num = { width: 90, padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit', textAlign: 'center' };
const txt = { width: '100%', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit', textAlign: 'right' };
const ibtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' };

function Field({ label, children, grow }) {
  return <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.78rem', color: '#6b7280', flex: grow ? '1 1 180px' : '0 0 auto' }}>{label}{children}</label>;
}
function Row({ label, v, bold }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.18rem 0', fontWeight: bold ? 700 : 400, fontSize: bold ? '0.92rem' : '0.85rem' }}><span>{label}</span><span>{v}</span></div>;
}
function RateRow({ label, v1, v2, on1, on2 }) {
  return (
    <tr style={{ borderTop: '1px solid #f0f0f3' }}>
      <td style={td}>{label}</td>
      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 90 }} value={v1} onChange={e => on1(+e.target.value)} /></td>
      <td style={tdC}><input type="number" min="0" style={{ ...num, width: 90 }} value={v2} onChange={e => on2(+e.target.value)} /></td>
    </tr>
  );
}

/** Customer-facing quote — prices only. Printable. */
function QuotePreview({ config, settings, customer, trip, calc, quoteNo: quoteNoProp, onClose }) {
  const today = new Date();
  const validUntil = new Date(today.getTime() + (settings.quote_validity_days || 0) * 86400000);
  const fmt = (d) => d.toLocaleDateString('he-IL');
  const quoteNo = quoteNoProp || `Q-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 900) + 100)}`;
  // Travel + work are folded into each item price (no separate labour line).
  const lines = calc.customerLines.map(l => ({ name: l.name, qty: l.qty, price: l.unit, total: l.total }));

  return (
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '1.5rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 820 }}>
        <div className="no-print" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <button className="btn btn-primary" onClick={() => window.print()}>הדפס / שמור PDF</button>
          <button className="btn btn-secondary" onClick={onClose}>סגור</button>
        </div>
        <div className="quote-print" style={{ background: '#fff', borderRadius: 10, padding: '2.5rem', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', color: '#1a1a2e', lineHeight: 1.7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #1B3A5C', paddingBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1B3A5C', lineHeight: 1.1 }}>{config.company.system}</div>
              <div style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.82rem', marginTop: 2 }}>בחסות {config.company.name}</div>
            </div>
            <div style={{ textAlign: 'left', fontSize: '0.85rem', color: '#6b7280' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1a1a2e' }}>הצעת מחיר</div>
              <div>מספר: {quoteNo}</div><div>תאריך: {fmt(today)}</div><div>בתוקף עד: {fmt(validUntil)}</div>
            </div>
          </div>
          <div style={{ margin: '1.25rem 0', fontSize: '0.92rem' }}>
            <strong>לכבוד:</strong> {customer.name || '—'}{customer.contact ? ` · ${customer.contact}` : ''}{customer.phone ? ` · ${customer.phone}` : ''}
            {trip.address ? <div style={{ color: '#6b7280' }}>כתובת: {trip.address}</div> : null}
          </div>
          {(config.value_headline || config.value_paragraph) && (
            <div className="quote-section" style={{ margin: '0 0 1.25rem' }}>
              {config.value_headline && (
                <div style={{ fontWeight: 800, color: '#1B3A5C', fontSize: '0.98rem', marginBottom: 6 }}>{config.value_headline}</div>
              )}
              {config.value_paragraph && (
                <p style={{ fontSize: '0.88rem', color: '#374151', margin: 0, lineHeight: 1.85 }}>{config.value_paragraph}</p>
              )}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead><tr style={{ background: '#f3f4f6' }}>
              <th style={qth}>תיאור</th><th style={{ ...qth, textAlign: 'center', width: 70 }}>כמות</th>
              <th style={{ ...qth, textAlign: 'center', width: 110 }}>מחיר ליח׳</th><th style={{ ...qth, textAlign: 'center', width: 120 }}>סה"כ</th>
            </tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={qtd}>{l.name}</td><td style={{ ...qtd, textAlign: 'center' }}>{l.qty}</td>
                  <td style={{ ...qtd, textAlign: 'center' }}>{ILS(l.price)}</td><td style={{ ...qtd, textAlign: 'center', fontWeight: 600 }}>{ILS(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '1rem' }}>
            <table style={{ minWidth: 280, fontSize: '0.9rem' }}>
              <tbody>
                <tr style={{ borderTop: '2px solid #1B3A5C' }}>
                  <td style={{ ...qtot, fontWeight: 800, fontSize: '1.05rem' }}>סה"כ (לא כולל מע"מ)</td>
                  <td style={{ ...qtot, textAlign: 'left', fontWeight: 800, fontSize: '1.05rem', color: '#1B3A5C' }}>{ILS(calc.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.6rem' }}>* {config.not_included_note}</p>
          {config.included_note && (
            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.3rem' }}>{config.included_note}</p>
          )}
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>הכנות באחריות הלקוח</div>
            <ul style={{ margin: '0.5rem 0 0', paddingRight: '1.2rem', fontSize: '0.88rem' }}>
              {config.prep_items.map((p, i) => <li key={i} style={{ marginBottom: 4 }}>{p}</li>)}
            </ul>
          </div>
          {config.faults && (
            <div className="quote-section" style={{ marginTop: '1.5rem' }}>
              <div style={qsec}>{config.faults.title}</div>
              <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: '0.4rem' }}>{config.faults.text}</p>
            </div>
          )}
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>תנאי תשלום</div>
            <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: '0.4rem' }}>{settings.payment_terms}</p>
          </div>
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>אספקה והתקנה</div>
            <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: '0.4rem' }}>
              אספקה, הזמנת ציוד, הכנות ייצור והתקנה תוך {settings.lead_time_days} ימים ממועד אישור ההצעה ועד מסירת ההתקנה.
            </p>
          </div>
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>אחריות ושירות</div>
            <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: '0.4rem' }}>{settings.warranty_text}</p>
          </div>
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>תמיכה</div>
            <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: '0.4rem' }}>
              תמיכה טכנית בטלפון: {settings.support_phone}.
            </p>
          </div>
          <div className="quote-section" style={{ marginTop: '1.5rem' }}>
            <div style={qsec}>{config.confidentiality.title}</div>
            <p style={{ fontSize: '0.82rem', color: '#374151', marginTop: '0.4rem' }}>{config.confidentiality.text}</p>
          </div>
          <div style={{ marginTop: '2.5rem', fontSize: '0.85rem' }}>
            <div style={{ width: '45%' }}><div style={{ borderTop: '1px solid #9ca3af', paddingTop: 4 }}>חתימת הלקוח + תאריך</div></div>
          </div>
          <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af' }}>
            {config.company.name} · {config.company.system} · {config.company.email} · {config.company.website}
          </div>
        </div>
      </div>
    </div>
  );
}

const qth = { padding: '0.55rem 0.7rem', textAlign: 'right', fontWeight: 700, borderBottom: '2px solid #ddd' };
const qtd = { padding: '0.5rem 0.7rem' };
const qtot = { padding: '0.3rem 0.7rem' };
const qsec = { fontWeight: 800, color: '#1B3A5C', borderRight: '4px solid #F97316', paddingRight: 8 };

export default PricingCalculator;
